<template>
  <div class="clerk-console">
    <ClerkTopBar
      label="Clerk"
      :sale-name="event.auctionName"
      :lane="event.lane"
      :external-id="event.extId"
      :stream-health="event.streamHealth"
    >
      <template #actions>
        <Tag v-if="runtimeFailure" severity="danger" :value="runtimeFailure" class="clerk-console__runtime-notice" />
      </template>
      <!-- The shared shell's avatar, which opens the App Menu: the profile,
           Settings (theme and language) and Log Out, as on every other screen.
           The drawer itself is mounted at the app's root. -->
      <template #account>
        <AvatarMenu />
      </template>
    </ClerkTopBar>

    <div class="clerk-console__stage">
      <div class="clerk-console__main">
        <OnBlockPanel
          v-model="lights"
          :run-number="railRunNumber"
          :vehicle="railAsset"
          :valuation="valuation"
          :bid-history="bidHistory"
        />

        <!-- Everything the confirm panel covers: the pad, the centre column and
             the ladder, but never the on-block panel — the clerk keeps reading the
             car while they settle it. -->
        <div class="clerk-console__action-zone">
          <div class="clerk-console__centre">
            <div class="clerk-console__bid-pad">
              <FloorBidPad
                :current-bid="padCurrentBid"
                :asking-bid="padAskingBid"
                :increment="padIncrement"
                :phase="phase"
                :disabled="finalized || proposing"
                @bid="proposeBid"
              />
            </div>
            <div class="clerk-console__centre-column">
              <TimeOnBlock :key="onBlockItemId" />
              <RepPriceStrip
                :reserve-value="reservePrice ?? 0"
                :reserve-is-new="reserveIsNew"
                :black-book-value="blackBookValue"
                :proxy-bid-count="proxyBidCount"
                @clear-proxy-bids="clearProxyBids"
                @reset-bidding="resetBidding"
              />
              <ActionGrid
                :current-bid="currentBid ?? 0"
                :next-bid="nextBid || 0"
                :leader-source="mockItem.leader"
                :leader-badge="badge ?? null"
                :status="bidStatus"
                :can-undo="canUndo"
                :can-floor-override="canFloorOverride"
                :busy="acting"
                :rep-name="mockItem.rep?.name ?? null"
                @undo="undo"
                @floor-override="floorOverride"
                @take-floor="takeFloor"
                @finalize="beginFinalize"
              />
            </div>
          </div>

          <IncrementRail
            :steps="incrementSteps"
            :selected-step="selectedIncrement"
            :phase="phase"
            :disabled="finalized || settingIncrement"
            @select="setIncrement"
          />

          <ConfirmSalePanel
            :open="confirming.open || finalized"
            :read-only="finalized"
            :final-state="finalized ? bidStatus : confirming.finalState"
            :bid="finalized ? currentBid : confirming.bid"
            :badge="finalized ? badge : confirming.badge"
            :submitting="confirming.submitting"
            :error-message="confirming.errorMessage"
            @confirm="finalizeSale"
            @cancel="cancelFinalize"
          />
        </div>
      </div>

      <ClerkBottomBar
        v-model="typedRunNumber"
        :event-started-at-seconds="EVENT_STARTED_AT_SECONDS"
        :can-go-previous="!!previousItemId && !moving"
        :can-go-next="!!nextItemId && !moving"
        @halt="stopDrawerOpen = true"
        @previous="moveToItem(previousItemId)"
        @next="moveToItem(nextItemId)"
        @run-search="searchRun"
      />

      <div class="clerk-console__band">
        <div class="clerk-console__chat">
          <ChatPanel
            v-model="activeConversationId"
            :conversations="conversations"
            :messages="conversationMessages"
            :roster="roster"
            :composer-placeholder="composerPlaceholder"
            full-height
          />
        </div>
        <StatsPanel :stats="stats" />
        <UpNextPanel
          :items="runListRows"
          :found-run="foundRun"
          :moved="foundRunMoved"
          :target-event-name="event.auctionName"
          :target-lane="event.lane"
          @move="(item) => moveToItem(item.id)"
          @cross-event-move="acceptCrossEventMove"
          @cross-event-close="foundRun = null"
        />
      </div>

      <!-- Covers the stage but not the top bar — the clerk still sees which sale
           they are in while no action is possible. -->
      <EventHaltedOverlay
        v-if="halted"
        :status="eventStatus"
        :resuming="resuming"
        @resume="resumeEvent"
        @leave="leave"
      />
    </div>

    <StopEventDrawer
      :open="stopDrawerOpen"
      :pausing="pausing"
      :ending="ending"
      @pause="pauseEvent"
      @end="stopEvent"
      @close="stopDrawerOpen = false"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import Tag from 'primevue/tag';
import {
  ActionGrid,
  ChatPanel,
  ClerkBottomBar,
  ClerkTopBar,
  ConfirmSalePanel,
  EventHaltedOverlay,
  FloorBidPad,
  IncrementRail,
  RepPriceStrip,
  OnBlockPanel,
  StatsPanel,
  StopEventDrawer,
  TimeOnBlock,
  UpNextPanel,
} from '@hds/auto-auction-com-components-desktop';
import {
  adminEventRuntimeApiSdk,
  useAdminItemBiddingStore,
} from '@hds/admin-client-pinia-vue-sdk';

import { AvatarMenu } from '@hds/common-ui-lib/desktop';
import { CLERK_DATA, clerkLookupRun } from './clerkData.js';
import { closeEvent, openEvent } from './clerkRuntime.js';
import { useEvent } from './composables/useEvent.js';
import { useAsset } from './composables/useAsset.js';
import { useSaleAndAssetsStore } from './stores/sale-and-assets.store.js';

// The clerk console, wired — the runtime an auction clerk drives a live lane
// from, opened from the Live Sales page and given the whole viewport by the
// shell (the route's `meta.immersive`).
//
// EVERY COMPONENT IT RENDERS IS PRESENTATION ONLY. They live in
// `@hds/auto-auction-com-components-desktop` and were lifted out of this
// application; the runtime calls each of them used to make from inside its own
// file — take the floor, set an increment, finalize a sale, pause the event —
// all land here, which is the only layer that knows there IS a runtime.
//
// That is why this file is long and none of them are. It is the join between an
// event-runtime SDK and a component library, and the join has to live
// somewhere: a component that calls `adminEventRuntimeApiSdk` cannot be
// rendered in a catalogue, cannot be tested without a backend, and cannot be
// reused by the next surface that needs a bid pad.
//
// Live data still arrives through the composables next door
// (`useEvent` → `assetOnBlockId` → `useAsset`), and everything below the
// mock fallback is the same derivation the components used to do for
// themselves.
//
// It came from the legacy clerk webapp, which this app retired. What changed
// in the move: the event comes from the route rather than a query string, the
// runtime is the one `configureBackends` already pointed at (clerkRuntime.js),
// the top bar's account corner is the shared shell's Avatar Menu (the theme is
// set from the App Menu's Settings, like everywhere else), and leaving goes back
// to Live Sales.
defineOptions({ name: 'ClerkConsole' });

const props = defineProps({
  // The runtime's event id — the lane this console drives.
  eventId: { type: String, required: true },
});

const router = useRouter();

// ── The runtime ─────────────────────────────────────────────────────────────
//
// Connect on arrival, watch this event, and let go on the way out. The console
// still renders from its mock when the runtime cannot be reached — a cockpit
// that shows nothing tells the clerk less than one that says why — so the
// failure is a notice in the top bar rather than a blank screen.

const runtimeFailure = ref(null);

onMounted(async () => {
  try {
    await openEvent(props.eventId);
  } catch (error) {
    console.error('[clerk] event runtime unavailable', error);
    runtimeFailure.value = `Runtime unavailable: ${error.message ?? error}`;
  }
});

onBeforeUnmount(() => {
  closeEvent(props.eventId).catch((error) => console.warn('[clerk] unwatch failed', error));
});

// ── The asset on the block ──────────────────────────────────────────────────

const eventId = computed(() => props.eventId);
const { sale, runListItemIds, nextItemId, prevItemId: previousItemId, assetOnBlockId, eventStatus } = useEvent(eventId);

// The tab names the lane being clerked (ruled 2026-09-22): the console opens
// in its own tab, so the title is how a clerk tells two of them apart.
const DEFAULT_TITLE = 'AutoAuction.com';
watch(
  () => sale.value,
  (info) => {
    const parts = ['Clerk', info?.laneName, info?.saleName].filter(Boolean);
    document.title = parts.length > 1 ? parts.join(' · ') : `Clerk · ${DEFAULT_TITLE}`;
  },
  { immediate: true }
);
onBeforeUnmount(() => {
  document.title = DEFAULT_TITLE;
});

// The mock is the offline fallback, and it is also the source for the fields no
// live feed fills in yet — the spec sheet's lot/stock/colour/drivetrain, the
// selling rep, the chat. Where a live value exists it wins.
const mockItem = computed(() => CLERK_DATA.current);
const event = computed(() => CLERK_DATA.event);
const onBlockItemId = computed(() => assetOnBlockId.value ?? mockItem.value.id);

const {
  asset,
  runNumber,
  bidHistory,
  blackBookValue,
  biddingInfo,
  currentBid,
  nextBid,
  badge,
  bidStatus,
  increment,
  proxyBidCount,
  reservePrice,
} = useAsset(onBlockItemId);

const live = computed(() => !!biddingInfo.value);

// The asset the rail renders: live fields over the mock's, which is the merge
// `OnBlockPanel` used to do inside itself. Reconciling two feeds' field
// names is application work, so it happens here.
const railAsset = computed(() => {
  const loaded = asset.value;
  const fallback = mockItem.value;
  return {
    year: loaded?.year ?? fallback.year,
    title: [loaded?.make, loaded?.model].filter(Boolean).join(' ') || fallback.title,
    vin: loaded?.vin ?? fallback.vin,
    miles: loaded?.mileage ?? fallback.miles,
    lotNumber: fallback.lotNumber,
    lane: fallback.lane,
    timesRun: fallback.timesRun,
    stockNumber: fallback.stockNumber,
    color: fallback.color,
    drivetrain: fallback.drivetrain,
    cylinders: fallback.cylinders,
    transmission: fallback.transmission,
    airConditioning: fallback.airConditioning,
    seller: fallback.seller,
    sellerBadge: fallback.sellerBadge,
  };
});

const railRunNumber = computed(() => runNumber.value ?? mockItem.value.runNumber);

const valuation = computed(() => ({
  blackBookValue: blackBookValue.value,
  marketReportValue: mockItem.value.mmr,
  reserveValue: reservePrice.value ?? mockItem.value.reserve,
}));

// The lights are editable in place and are not round-tripped to the runtime
// yet. Re-seeded from the asset whenever the block advances, so the editor
// never shows the previous asset's disclosures.
const lights = ref([]);
watch(
  [() => asset.value?.lights, onBlockItemId],
  ([loaded]) => {
    lights.value = loaded ?? mockItem.value.lights ?? [];
  },
  { immediate: true }
);

// ── Bidding phase ───────────────────────────────────────────────────────────

const FINALIZED_STATUSES = ['SOLD', 'NOT-SOLD', 'OFFER'];
const finalized = computed(() => FINALIZED_STATUSES.includes(bidStatus.value));
const hasBids = computed(() => (live.value ? !!badge.value : mockItem.value.currentBid > 0));

// `bidding` once a bid is standing, `asking` while the auctioneer is still
// fishing for an opening one. It decides which way the pad and the ladder run,
// and which route a tapped price takes.
const phase = computed(() => (hasBids.value ? 'bidding' : 'asking'));

const padCurrentBid = computed(() => {
  if (!live.value) return hasBids.value ? mockItem.value.currentBid : null;
  return hasBids.value ? currentBid.value : null;
});
const padAskingBid = computed(() => (live.value ? nextBid.value ?? 0 : mockItem.value.askingBid));
const padIncrement = computed(() => (live.value ? increment.value : mockItem.value.nextMinIncrement));

// Two ladders, because the two phases step by different amounts: an increment
// ladder starts fine, a drop ladder starts coarse.
const BIDDING_STEPS = [25, 50, 100, 250, 500, 1000, 2500, 5000];
const ASKING_STEPS = [50, 100, 200, 500, 1000, 2000, 2500, 5000];
const incrementSteps = computed(() => (hasBids.value ? BIDDING_STEPS : ASKING_STEPS));

// The ladder highlights the rung NEAREST the live increment, since the runtime
// is free to hold a value that is not on any rung.
const selectedIncrement = computed(() => {
  const target = increment.value ?? mockItem.value.nextMinIncrement ?? 0;
  if (!target) return null;
  return incrementSteps.value.reduce(
    (closest, step) => (Math.abs(step - target) < Math.abs(closest - target) ? step : closest),
    incrementSteps.value[0]
  );
});

// Floor Override only applies once a real buyer holds the high bid — 'FLOOR' is
// the runtime's placeholder, not a bidder to override.
const canFloorOverride = computed(() => !!badge.value && String(badge.value).toUpperCase() !== 'FLOOR');

// The engine is authoritative and rejects true no-ops, so this only has to stop
// the obviously-pointless presses.
const canUndo = computed(() => !finalized.value && (hasBids.value || bidStatus.value === 'BIDDING'));

// ── The reserve the asset came onto the block with ──────────────────────────
//
// A selling rep can move the reserve mid-sale, and the clerk has to be told. The
// comparison needs a value remembered from before the change, which is state
// about an asset rather than about a render — so it is here rather than in the
// strip that draws it.

const openingReserve = ref(null);
watch(onBlockItemId, () => {
  openingReserve.value = null;
});
watch(
  reservePrice,
  (value) => {
    if (value != null && openingReserve.value == null) openingReserve.value = value;
  },
  { immediate: true }
);
const reserveIsNew = computed(
  () => reservePrice.value != null && openingReserve.value != null && reservePrice.value !== openingReserve.value
);

// ── Runtime calls ───────────────────────────────────────────────────────────
//
// One guard shape for all of them: refuse to re-enter, log, and always clear.
// The console is a cockpit — a button that silently stops responding because a
// flag was left set is worse than one that fails loudly.

const acting = ref(false);
const proposing = ref(false);
const settingIncrement = ref(false);
const moving = ref(false);

async function call(flag, description, action) {
  if (flag.value) return false;
  flag.value = true;
  try {
    await action();
    return true;
  } catch (error) {
    console.error(`[clerk] ${description} failed`, error);
    return false;
  } finally {
    flag.value = false;
  }
}

// A price tapped on the pad means two different things depending on the phase:
// while the auctioneer is asking it MOVES the ask, and after that it records a
// floor bid. The pad reports one `bid` event and this picks the route.
const proposeBid = (amount) => {
  if (!amount || finalized.value) return;
  return call(proposing, 'proposeBid', () =>
    bidStatus.value === 'ASKING'
      ? adminEventRuntimeApiSdk.clerkSetAskingPrice(onBlockItemId.value, amount)
      : adminEventRuntimeApiSdk.clerkProposeBid(onBlockItemId.value, amount)
  );
};

const takeFloor = () => {
  if (finalized.value) return;
  return call(acting, 'clerkProposeBid (floor)', () =>
    adminEventRuntimeApiSdk.clerkProposeBid(onBlockItemId.value, nextBid.value)
  );
};

const floorOverride = () => {
  if (finalized.value || !canFloorOverride.value) return;
  return call(acting, 'clerkFloorOverride', () => adminEventRuntimeApiSdk.clerkFloorOverride(onBlockItemId.value));
};

const undo = () => {
  if (!canUndo.value) return;
  return call(acting, 'clerkUndo', () => adminEventRuntimeApiSdk.clerkUndo(onBlockItemId.value));
};

const setIncrement = (amount) => {
  if (finalized.value) return;
  return call(settingIncrement, 'clerkSetBidIncrement', () =>
    adminEventRuntimeApiSdk.clerkSetBidIncrement(onBlockItemId.value, amount)
  );
};

const clearProxyBids = () =>
  call(acting, 'clerkDeleteProxyBids', () => adminEventRuntimeApiSdk.clerkDeleteProxyBids(onBlockItemId.value));

const resetBidding = () =>
  call(acting, 'clerkResetBidding', () => adminEventRuntimeApiSdk.clerkResetBidding(onBlockItemId.value));

const moveToItem = (itemId) => {
  if (!itemId) return;
  return call(moving, 'moveToItem', () => adminEventRuntimeApiSdk.moveToItem(eventId.value, itemId));
};

// ── Settling an asset ───────────────────────────────────────────────────────
//
// Pressing Sell / Offer / No Sale does NOT settle anything. It freezes bidding
// and raises the confirm panel with the bid and badge as they were at the press
// — because what gets recorded has to be what the clerk saw when they decided,
// and live state can move in the time it takes to read the panel.

const confirming = reactive({
  open: false,
  finalState: null,
  bid: null,
  badge: null,
  submitting: false,
  errorMessage: null,
});

async function beginFinalize(finalState) {
  if (confirming.submitting || finalized.value) return;
  const captured = { finalState, bid: currentBid.value, badge: badge.value };
  const paused = await call(acting, `pauseSale (for ${finalState})`, () =>
    adminEventRuntimeApiSdk.pauseSale(onBlockItemId.value, 'Finalizing sale')
  );
  if (!paused) return;
  Object.assign(confirming, { ...captured, open: true, submitting: false, errorMessage: null });
}

async function finalizeSale({ finalState, bid, badge: winningBadge }) {
  confirming.submitting = true;
  confirming.errorMessage = null;
  try {
    await adminEventRuntimeApiSdk.finalizeSale(onBlockItemId.value, finalState, bid, winningBadge);
    confirming.open = false;
  } catch (error) {
    console.error(`[clerk] finalizeSale (${finalState}) failed`, error);
    confirming.errorMessage = 'Could not finalize the sale. Try again.';
  } finally {
    confirming.submitting = false;
  }
}

// Cancel resumes the frozen sale and records nothing. It stays open on failure
// rather than dismissing: an asset stranded paused with the panel gone has no way
// to be unstuck.
async function cancelFinalize() {
  confirming.submitting = true;
  confirming.errorMessage = null;
  try {
    await adminEventRuntimeApiSdk.resumeSale(onBlockItemId.value);
    confirming.open = false;
  } catch (error) {
    console.error('[clerk] resumeSale failed', error);
    confirming.errorMessage = 'Could not resume the sale. Try again.';
  } finally {
    confirming.submitting = false;
  }
}

// ── Stopping the sale ───────────────────────────────────────────────────────

const stopDrawerOpen = ref(false);
const pausing = ref(false);
const ending = ref(false);
const resuming = ref(false);

const halted = computed(() => eventStatus.value === 'PAUSED' || eventStatus.value === 'FINISHED');

// Both routes require a message; the sheet leaves the reason optional, so the
// fallback is supplied here — this is the layer that knows which route it is
// about to call.
async function pauseEvent(reason) {
  const paused = await call(pausing, 'pauseEvent', () =>
    adminEventRuntimeApiSdk.pauseEvent(eventId.value, reason || 'Paused by clerk')
  );
  if (paused) stopDrawerOpen.value = false;
}

async function stopEvent(reason) {
  const stopped = await call(ending, 'stopEvent', () =>
    adminEventRuntimeApiSdk.stopEvent(eventId.value, reason || 'Ended by clerk')
  );
  if (stopped) stopDrawerOpen.value = false;
}

const resumeEvent = () => call(resuming, 'resumeEvent', () => adminEventRuntimeApiSdk.resumeEvent(eventId.value));

// The only way out of a finished sale: there is nothing left for the console to
// drive, so hand the clerk back to the Live Sales page they came from.
const leave = () => router.push({ name: 'live-sales' });

// ── The run list ────────────────────────────────────────────────────────────

const saleAndAssetsStore = useSaleAndAssetsStore();
const itemBiddingStore = useAdminItemBiddingStore();

// Assets pulled in from another event, kept alongside the live run list rather
// than merged into it — the runtime has not been told about them yet.
const movedInRows = ref([]);

const runListRows = computed(() => {
  const ids = runListItemIds.value;
  if (!ids?.length) {
    return [...movedInRows.value, ...CLERK_DATA.upNext.map((row) => ({ ...row, id: row.run }))];
  }

  const rows = ids.map((id) => {
    const found = saleAndAssetsStore.assets[id];
    const bidding = itemBiddingStore.items[id];
    return {
      id,
      run: found?.runNumber ?? id,
      year: found?.year,
      title: [found?.make, found?.model].filter(Boolean).join(' '),
      repRequired: false,
      onBlock: id === assetOnBlockId.value,
      highBid: bidding?.bid ?? null,
      badge: bidding?.badge ?? null,
      status: bidding?.status ?? null,
    };
  });

  return [...movedInRows.value, ...rows];
});

// Which runtime statuses count as sold, and which merely as finished. This is
// the runtime's vocabulary, so the tally lives here and `StatsPanel` renders it.
const SOLD_STATUSES = ['SOLD', 'SOLD-BUYNOW', 'OFFER'];
const SETTLED_STATUSES = ['SOLD', 'SOLD-BUYNOW', 'OFFER', 'NOT-SOLD', 'ABORTED'];

const stats = computed(() => {
  const ids = runListItemIds.value;
  if (!ids?.length) {
    const { items, itemsRun, itemsSold, itemsSoldIf, totalSales, sellThrough } = CLERK_DATA.stats;
    return { items, itemsRun, itemsSold, itemsSoldOnOffer: itemsSoldIf, totalSales, sellThrough };
  }

  let itemsRun = 0;
  let itemsSold = 0;
  let itemsSoldOnOffer = 0;
  let totalSales = 0;

  for (const id of ids) {
    const bidding = itemBiddingStore.items[id];
    const status = bidding?.status;
    if (!status) continue;
    if (SETTLED_STATUSES.includes(status)) itemsRun += 1;
    if (SOLD_STATUSES.includes(status)) {
      itemsSold += 1;
      totalSales += bidding?.bid ?? 0;
      if (status === 'OFFER') itemsSoldOnOffer += 1;
    }
  }

  return {
    items: ids.length,
    itemsRun,
    itemsSold,
    itemsSoldOnOffer,
    totalSales,
    sellThrough: ids.length ? itemsSold / ids.length : 0,
  };
});

// ── Finding a run ───────────────────────────────────────────────────────────
//
// The search field reports a number; resolving it is this layer's job. A run in
// this event does nothing yet — the clerk can see it in the list. A run in
// ANOTHER event raises the sheet, because a clerk typing a number off a printed
// sheet needs to be told the asset is real but elsewhere, not shown "no results".

const typedRunNumber = ref('');
const foundRun = ref(null);
const foundRunMoved = ref(false);

function searchRun(value) {
  const result = clerkLookupRun(value, { current: mockItem.value, upNext: runListRows.value });
  if (result.kind !== 'cross') return;
  foundRun.value = result.item;
  foundRunMoved.value = false;
}

const CONFIRMATION_DWELL_MILLISECONDS = 1100;

function acceptCrossEventMove() {
  const found = foundRun.value;
  if (!found) return;
  if (!movedInRows.value.some((row) => String(row.run) === String(found.run))) {
    movedInRows.value = [{ ...found, id: `moved-${found.run}`, highlighted: true }, ...movedInRows.value];
  }
  foundRunMoved.value = true;
  setTimeout(() => {
    foundRun.value = null;
  }, CONFIRMATION_DWELL_MILLISECONDS);
}

// ── The lane chat ───────────────────────────────────────────────────────────
//
// Still reading the mock. The panel renders whatever it is given, so pointing it
// at a real transport is a change to these three computeds and nothing else.

const activeConversationId = ref('global');

const conversations = computed(() => [
  { id: 'global', label: 'Global', sub: 'Everyone in the sale', count: CLERK_DATA.chat.global?.length ?? 0 },
  { id: 'rep', label: 'Rep', sub: mockItem.value.rep?.name ?? 'Selling rep', count: CLERK_DATA.chat.rep?.length ?? 0 },
]);

const conversationMessages = computed(() =>
  (CLERK_DATA.chat[activeConversationId.value] ?? []).map((message) => ({
    id: message.id,
    author: message.who,
    role: message.role,
    text: message.text,
    time: message.t,
    mine: message.role === 'clerk',
  }))
);

const roster = computed(() => {
  const activity = CLERK_DATA.activity ?? {};
  const everyone = [...(activity.hovering ?? []), ...(activity.bidded ?? []), ...(activity.viewing ?? [])];
  const byBadge = new Map();
  for (const attendee of everyone) {
    if (!byBadge.has(attendee.badge)) byBadge.set(attendee.badge, attendee);
  }
  return [...byBadge.values()];
});

const composerPlaceholder = computed(() =>
  activeConversationId.value === 'rep'
    ? `Message ${mockItem.value.rep?.name ?? 'the rep'}…`
    : 'Message everyone in the lane…'
);

// ── Pace ────────────────────────────────────────────────────────────────────
//
// The sale's elapsed time is not on the runtime's event yet, so the clock seeds
// from the reference's figure and counts up from there. `TimeOnBlock` is keyed
// on the asset so it restarts as the block advances, which is the behaviour that
// matters — a stale "time on block" is worse than none.

const EVENT_STARTED_AT_SECONDS = 5247;
</script>

<style scoped>
/* The console's geometry — one non-scrolling screen laid out
   position-for-position with the incumbent Velocicast clerk. This lived in a
   `ReplacementClerkScreen` layout component; it is application composition, not
   a reusable component, so it moved here with the wiring it frames.
   As an application view this reads the semantic tier directly.

     ┌─ top bar ──────────────────────────────────────────────────────────┐
     │ on-block panel │ bid pad · centre column │ increment ladder        │
     ├─ event bar ────────────────────────────────────────────────────────┤
     │ chat            │ stats            │ run list                      │
     └────────────────────────────────────────────────────────────────────┘ */
.clerk-console {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  background: var(--surface-ground);
  color: var(--p-text-color);
  font-family: var(--p-font-body);
  font-size: 14px;
}

.clerk-console__stage {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  /* Containing block for the halted overlay, which covers the stage and leaves
     the top bar reachable. */
  position: relative;
  padding: 16px;
  gap: 12px;
}

.clerk-console__main {
  flex: 3;
  min-height: 0;
  display: flex;
  gap: 12px;
  /* `clip`, not `hidden`: below the console's comfortable width the on-block panel
     hits its floor, and a column must be clipped rather than paint outside its
     row — but this must never become a SCROLL container, or focusing a field
     inside the confirm panel scrolls the row and yanks the on-block panel sideways. */
  overflow: clip;
}

.clerk-console__action-zone {
  /* Grows 2 against the on-block panel's 1, from a basis of the ladder's own width:
     the action zone has to fit the pad, the grid AND the ladder, so it wins
     contested space — otherwise the panel balloons into its empty bid-history area and
     squeezes the action grid under the ladder. */
  flex: 2 1 207px;
  min-width: 0;
  min-height: 0;
  display: flex;
  /* Containing block for the confirm panel, which covers this zone and nothing
     else. */
  position: relative;
  gap: 12px;
}

.clerk-console__centre {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  gap: 12px;
}

.clerk-console__bid-pad {
  flex: 0 0 360px;
  min-width: 0;
  display: flex;
}

.clerk-console__centre-column {
  flex: 1;
  min-height: 0;
  /* Shrink rather than overflow: without this the action grid keeps its
     intrinsic width when space is tight and spills under the ladder. */
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.clerk-console__band {
  flex: 1.05;
  min-height: 0;
  display: flex;
  gap: 12px;
}

.clerk-console__chat {
  flex: 0 0 380px;
  min-height: 0;
  display: flex;
}

.clerk-console__runtime-notice {
  max-width: 320px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
